'use client'

import React, { useEffect, useRef } from 'react'
import { cn } from 'lib/utils'
import { Check } from 'lucide-react'
import { startCase } from 'lodash'

interface TimelinePhase {
  datetime: string
  type: string
}

interface TimelineSectionProps {
  timeline: TimelinePhase[]
}

const TIMELINE_LABELS: Record<string, string> = {
  nftClaiming: 'NFT Claiming',
  nftTransferable: 'NFT Transferable',
}

const getPhaseStatus = (
  datetime: string,
  index: number,
  timeline: TimelinePhase[],
): 'completed' | 'active' | 'upcoming' => {
  const now = new Date()
  const phaseDate = new Date(datetime)
  const nextPhaseDate =
    index < timeline.length - 1 ? new Date(timeline[index + 1].datetime) : null

  // If phase date is in the future, it's upcoming
  if (phaseDate > now) {
    return 'upcoming'
  }

  // If there's a next phase and we haven't reached it yet, current phase is active
  if (nextPhaseDate && now < nextPhaseDate) {
    return 'active'
  }

  // If there's a next phase and we've passed it, this phase is completed
  if (nextPhaseDate && now >= nextPhaseDate) {
    return 'completed'
  }

  // If this is the last and it has started, keep it active else completed
  return now < phaseDate ? 'active' : 'completed'
}

export function TimelineSection({ timeline }: TimelineSectionProps) {
  console.log(timeline)
  const timelineRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (timelineRef.current) {
      const activeIndex = timeline.findIndex(
        (phase, index) =>
          getPhaseStatus(phase.datetime, index, timeline) === 'active',
      )
      if (activeIndex !== -1) {
        const activeElement = timelineRef.current.children[
          activeIndex
        ] as HTMLElement
        if (activeElement) {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          })
        }
      }
    }
  }, [timeline])

  return (
    <div className="flex flex-col gap-2 bg-neutral-white rounded-lg border border-neutral-200 shadow-none p-6">
      <h3 className="font-semibold text-neutral-950 mb-[2px] text-lg flex items-center">
        Distribution Timeline
        <span className="font-normal ml-2 text-xs text-neutral-400">
          (UTC+8 timezone)
        </span>
      </h3>
      <div
        ref={timelineRef}
        className="flex items-start gap-1 relative overflow-x-auto scrollbar-hide px-2"
        style={{ scrollBehavior: 'smooth' }}
      >
        {timeline.map((phase, index) => {
          const status = getPhaseStatus(phase.datetime, index, timeline)
          console.log(status, 'status')
          return (
            <React.Fragment key={phase.type}>
              <div className="flex flex-col items-center gap-2 text-center min-w-[90px] pt-1.5">
                <div
                  className={cn(
                    'flex items-center justify-center w-[18px] h-[18px] rounded-full relative',
                    status === 'completed' && 'bg-neutral-400',
                    status === 'active' && 'bg-brand-primary',
                    status === 'upcoming' && 'bg-transparent',
                  )}
                >
                  {status === 'completed' && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                  {status === 'active' && (
                    <>
                      <div className="absolute inset-0 w-[18px] h-[18px] rounded-full bg-brand-primary/50 animate-ping z-0" />
                      <div className="w-1.5 h-1.5 bg-white rounded-full relative z-10" />
                    </>
                  )}
                  {status === 'upcoming' && (
                    <div className="w-2 h-2 bg-neutral-200 rounded-full" />
                  )}
                </div>
                <div
                  className={cn(
                    'text-xs',
                    status === 'active' && 'text-brand-primary font-medium',
                    status === 'completed' && 'text-neutral-600',
                    status === 'upcoming' && 'text-neutral-400',
                  )}
                >
                  {TIMELINE_LABELS[phase.type] ?? startCase(phase.type)}
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <div className="text-xs text-neutral-500">
                    {new Date(phase.datetime).toLocaleDateString('en-US', {
                      hour: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              </div>
              {index < timeline.length - 1 && (
                <div className="flex-1 h-px bg-neutral-200 mt-2 min-w-[30px]" />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
